import { useMemo } from 'react';
import type { InboxMaterialRow, SettingsMaterial } from '@coffman-tm/shared';
import { useSettingsMaterials } from '../../lib/useSettings';
import { formatMoneyCents } from '../../lib/format';
import { lineCents } from './blockSubtotals';

/**
 * Per-block materials, read-only.
 *
 * Until 2026-08-03 this was a triage surface: the parser scanned the foreman's
 * notes for material candidates and each one arrived here as a card with a
 * quoted phrase, a Material dropdown, Quantity/Rate inputs, and an accept /
 * reject pair of buttons — and an undecided card blocked the block's save.
 *
 * That whole pass is gone (client ruling; register entry M12). Foremen write
 * prose, not material tables, so there is nothing in the text to detect: the
 * detector fired on lines like "Start placing rock for the area" and then held
 * a save hostage to a question with no answer on the page. The real rule is
 * that a block carries no materials unless the PM adds one, from knowledge the
 * report does not contain.
 *
 * So this component now only lists what actually bills — hand-added lines, plus
 * any parser candidate confirmed before the ruling. Undecided candidates are
 * still stored (the parser is unchanged) and are simply never shown. The one
 * control that survives is Remove, on a hand-added line, while the block is
 * still pending. New material lines come from "+ Add material" in the section
 * header, which is now the only way one is ever created.
 */
export interface InboxMaterialsListProps {
  /** Already filtered through `billableMaterialRows` by the caller. */
  rows: InboxMaterialRow[];
  editable: boolean;
  onRemoveManual?: (row: InboxMaterialRow) => void;
}

/**
 * The material lines that bill, and therefore the only ones a PM is shown.
 *
 * `'pending'` is a parser candidate nobody will ever decide — invisible by
 * design. `'skipped'` was a decision against a candidate under the old flow;
 * it bills nothing and the concept it belonged to no longer exists, so it is
 * invisible too. Exported so the block card can decide whether the Materials
 * section has anything in it without duplicating the rule.
 */
export function billableMaterialRows(rows: InboxMaterialRow[]): InboxMaterialRow[] {
  return rows.filter((r) => r.status === 'confirmed' || r.status === 'edited');
}

export function InboxMaterialsList({ rows, editable, onRemoveManual }: InboxMaterialsListProps) {
  const masters = useSettingsMaterials();

  if (rows.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 12, padding: '6px 0' }}>
        No materials on this block.
      </div>
    );
  }

  const materials = masters.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((row) => (
        <MaterialRow
          key={row.material_queue_item_id}
          row={row}
          materials={materials}
          editable={editable}
          onRemoveManual={onRemoveManual}
        />
      ))}
    </div>
  );
}

function MaterialRow({
  row,
  materials,
  editable,
  onRemoveManual,
}: {
  row: InboxMaterialRow;
  materials: SettingsMaterial[];
  editable: boolean;
  onRemoveManual?: (row: InboxMaterialRow) => void;
}) {
  const master = useMemo(
    () =>
      materials.find((m) => m.id === (row.confirmed_material_id ?? row.suggested_material_id)) ??
      null,
    [materials, row.confirmed_material_id, row.suggested_material_id],
  );
  const unit = master?.unit ?? row.suggested_unit ?? null;
  // A hand-added line stores the roster material's own name as its quoted_text,
  // so the label reads the same either way; the master is preferred because a
  // renamed roster entry should show its current name.
  const name = master?.name ?? row.suggested_name ?? row.quoted_text;
  const qty = row.confirmed_quantity ?? row.suggested_quantity ?? 0;
  const rateCents = row.confirmed_rate_cents ?? row.suggested_default_rate_cents ?? 0;
  const total = lineCents(qty, rateCents);

  return (
    <div
      style={{
        padding: '10px 14px',
        border: '1px solid var(--rule)',
        borderRadius: 6,
        background: 'var(--paper-2)',
        display: 'flex',
        gap: 12,
        alignItems: 'baseline',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--ink)', flex: '1 1 220px', minWidth: 0 }}>
        {name}
        {row.is_manual && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
              background: 'var(--surface)',
              border: '1px solid var(--rule)',
              padding: '1px 6px',
              borderRadius: 3,
            }}
          >
            manually added
          </span>
        )}
      </span>

      <Field label={`Quantity${unit ? ` (${unit})` : ''}`}>{qty}</Field>
      <Field label={`Rate ($/${unit ?? 'unit'})`}>
        {formatMoneyCents(rateCents, { precise: true })}
      </Field>
      <Field label="Line total">{formatMoneyCents(total, { precise: true })}</Field>

      {row.is_manual && editable && onRemoveManual && (
        <button
          type="button"
          className="btn-link"
          style={{ fontSize: 11, color: 'var(--danger)', marginLeft: 'auto' }}
          onClick={() => onRemoveManual(row)}
          title="Remove this manually-added line"
        >
          Remove
        </button>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span className="eyebrow" style={{ fontSize: 10 }}>
        {label}
      </span>
      <span className="mono" style={{ fontSize: 13, color: 'var(--ink)' }}>
        {children}
      </span>
    </span>
  );
}
